import React, { useState } from "react";
import axios, { API } from "@/lib/api";
import { toast } from "sonner";
import { curSym } from "../../utils/cur";

// 🛠️ POS order workbench row actions — lets staff (with can_take_online_orders)
// confirm payment, confirm wholesale pedidos (adjustable qty), ship and print
// documents without entering the admin backend.
export function OrderActions({ order, refresh, onDocs }) {
  const [qty, setQty] = useState(() => {
    const q = {};
    (order.items || []).forEach(i => { q[i.product_id] = i.quantity; });
    return q;
  });
  const [busy, setBusy] = useState(false);
  const [markPaid, setMarkPaid] = useState(!!order.payment_verified_source);

  const run = async (fn, okMsg, after) => {
    if (busy) return;
    setBusy(true);
    try {
      const r = await fn();
      toast.success(okMsg(r));
      refresh();
      if (after) after(r);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    }
    setBusy(false);
  };

  const confirmPay = () => run(
    () => axios.put(`${API}/shop/orders/${order.id}/confirm-payment`),
    () => "✅ Pago confirmado / 已确认收款");
  const ship = () => run(
    () => axios.put(`${API}/shop/orders/${order.id}/ship`),
    () => "🚚 Enviado / 已发货");
  const confirmWs = () => run(
    () => axios.post(`${API}/shop/orders/${order.id}/confirm-wholesale`, {
      items: Object.entries(qty).map(([product_id, quantity]) => ({ product_id, quantity: Number(quantity) || 0 })),
      mark_paid: order.payment_method === "credit" ? false : markPaid,
    }),
    (r) => `📦 Pedido confirmado / 已开单: ${r.data.ws_order_no} · ${curSym()}${Number(r.data.total || 0).toFixed(2)}`,
    (r) => { if (r.data.ws_order && onDocs) onDocs(r.data.ws_order); });

  const isWs = order.channel === "wholesale";
  const cancelled = order.order_status === "cancelled";
  const needWsConfirm = isWs && !order.ws_order_no && !cancelled && order.order_status !== "completed";

  return (
    <div className="mt-2 bg-slate-800/70 rounded-lg p-2 space-y-2" data-testid={`bell-actions-${order.id}`}>
      <ul className="space-y-1 max-h-44 overflow-y-auto">
        {(order.items || []).map((it, i) => (
          <li key={i} className="flex items-center gap-2 text-xs text-slate-300">
            <span className="flex-1 min-w-0 truncate">{it.product_name}{it.product_spec ? ` (${it.product_spec})` : ""}</span>
            {needWsConfirm ? (
              <input type="number" min="0" value={qty[it.product_id] ?? it.quantity}
                onChange={e => setQty(p => ({ ...p, [it.product_id]: e.target.value }))}
                className="w-14 bg-slate-700 border border-slate-600 rounded px-1 py-0.5 text-white text-xs text-center"
                data-testid={`bell-qty-${order.id}-${it.product_id}`} />
            ) : (
              <span className="text-slate-400">×{it.quantity}</span>
            )}
            <span className="text-emerald-400 w-16 text-right">{curSym()}{((it.unit_price || 0) * (Number(qty[it.product_id] ?? it.quantity) || 0)).toFixed(2)}</span>
          </li>
        ))}
      </ul>
      {needWsConfirm && order.payment_method !== "credit" && (
        <label className="flex items-center gap-2 text-xs text-slate-300 cursor-pointer" data-testid={`bell-paid-label-${order.id}`}>
          <input type="checkbox" checked={markPaid} onChange={e => setMarkPaid(e.target.checked)} className="accent-emerald-500" data-testid={`bell-paid-${order.id}`} />
          💵 已收款 / Pago recibido {!markPaid && <span className="text-amber-400">(未勾 → WS 未付)</span>}
        </label>
      )}
      <div className="flex flex-wrap gap-1.5">
        {needWsConfirm && (
          <button onClick={confirmWs} disabled={busy}
            className="text-xs px-2 py-1.5 rounded bg-amber-500 hover:bg-amber-600 text-black font-bold disabled:opacity-50"
            data-testid={`bell-confirm-ws-${order.id}`}>📦 Confirmar 开单</button>
        )}
        {order.payment_status === "pending" && !cancelled && !needWsConfirm && !isWs && (
          <button onClick={confirmPay} disabled={busy}
            className="text-xs px-2 py-1.5 rounded bg-emerald-600 hover:bg-emerald-700 text-white font-bold disabled:opacity-50"
            data-testid={`bell-confirm-pay-${order.id}`}>✅ Confirmar pago 收款</button>
        )}
        {order.payment_status === "paid" && order.order_status === "processing" && (
          <button onClick={ship} disabled={busy}
            className="text-xs px-2 py-1.5 rounded bg-purple-600 hover:bg-purple-700 text-white font-bold disabled:opacity-50"
            data-testid={`bell-ship-${order.id}`}>🚚 Enviar 发货</button>
        )}
        <button onClick={() => onDocs(order)}
          className="text-xs px-2 py-1.5 rounded bg-indigo-600 hover:bg-indigo-700 text-white font-bold"
          data-testid={`bell-docs-${order.id}`}>🖨️ Docs 打印</button>
      </div>
      {order.ws_order_no && <div className="text-[10px] text-blue-300">WS: {order.ws_order_no}</div>}
    </div>
  );
}
